import { FC, useCallback, useEffect, useRef, useState } from "react";
import ReactFlow, {
  Controls,
  Background,
  ReactFlowProvider,
  ConnectionLineType,
} from "reactflow";
import { Box, Flex } from "@radix-ui/themes";
import { Workflow, CreateUpdateWorkflow, WorkflowTrigger } from "@/types/breeze/Workflow";
import { useWorkflowEditor } from "./useWorkflowEditor";
import { WorkflowToolbar } from "./WorkflowToolbar";
import { StepNode } from "./StepNode";
import { StartNode } from "./StartNode";
import SideDrawer from "@/components/ui/SideDrawer";
import StepForm from "@/components/workflow/StepForm";
import Modal from "@/components/ui/Modal";
import WorkflowJobHistory from "../WorkflowJobHistory";
import WorkflowSettings from "../WorkflowSettings";
import InputField from "@/components/form/InputField";
import { workflowService } from "@/services/breeze/WorkflowService";
import { toast } from "react-hot-toast";
import "reactflow/dist/style.css";
import TriggerWorkflowDialog from "../TriggerWorkflowDialog";

// Custom CSS for theme-aware React Flow controls
const reactFlowControlsStyles = `
  .react-flow__controls {
    background: var(--color-panel-solid) !important;
    border: 1px solid var(--gray-6) !important;
    border-radius: var(--radius-3) !important;
    box-shadow: var(--shadow-3) !important;
  }

  .react-flow__controls-button {
    background: transparent !important;
    border: none !important;
    color: var(--gray-12) !important;
    transition: background-color 0.2s ease !important;
  }

  .react-flow__controls-button:hover {
    background: var(--gray-3) !important;
    color: var(--gray-12) !important;
  }

  .react-flow__controls-button:disabled {
    color: var(--gray-8) !important;
    cursor: not-allowed !important;
  }

  .react-flow__controls svg {
    fill: currentColor !important;
  }

  @keyframes execution-spin {
    to { transform: rotate(360deg); }
  }

  .execution-indicator {
    display: none;
    position: absolute;
    top: -10px;
    right: -10px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    z-index: 20;
    border: 2px solid white;
    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  }

  .execution-running .execution-indicator,
  .execution-success .execution-indicator,
  .execution-failure .execution-indicator {
    display: flex;
  }

  .execution-running .execution-indicator {
    background: var(--accent-9);
  }

  .execution-running .execution-indicator::after {
    content: "";
    display: block;
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: execution-spin 0.7s linear infinite;
  }

  .execution-running .rt-Card {
    border-color: var(--accent-9) !important;
  }

  .execution-success .execution-indicator {
    background: var(--green-9);
  }

  .execution-success .execution-indicator::after {
    content: "✓";
    color: white;
    font-size: 13px;
    font-weight: bold;
    line-height: 1;
  }

  .execution-success .rt-Card {
    border-color: var(--green-9) !important;
  }

  .execution-failure .execution-indicator {
    background: var(--red-9);
  }

  .execution-failure .execution-indicator::after {
    content: "✗";
    color: white;
    font-size: 13px;
    font-weight: bold;
    line-height: 1;
  }

  .execution-failure .rt-Card {
    border-color: var(--red-9) !important;
  }

  .react-flow__edge {
    cursor: pointer;
  }

  .react-flow__edge:hover .react-flow__edge-path {
    stroke-width: 3 !important;
    filter: brightness(1.2);
  }

  .react-flow__edge.selected .react-flow__edge-path {
    stroke-width: 3 !important;
    filter: drop-shadow(0 0 4px currentColor);
  }
`;

const nodeTypes = {
  stepNode: StepNode,
  startNode: StartNode,
};

interface WorkflowEditorProps {
  workflow: Workflow;
  onWorkflowUpdate?: () => void;
  onEnableWorkflow?: () => Promise<void>;
  onDisableWorkflow?: () => Promise<void>;
}

export const WorkflowEditor: FC<WorkflowEditorProps> = ({
  workflow,
  onWorkflowUpdate,
  onEnableWorkflow,
  onDisableWorkflow,
}) => {
  const [isTriggerWorkflowDialogOpen, setIsTriggerWorkflowDialogOpen] =
    useState(false);
  const [edgeDeletePos, setEdgeDeletePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const handleTest = useCallback(
    () => setIsTriggerWorkflowDialogOpen(true),
    [],
  );
  const {
    nodes,
    edges,
    workflowState,
    canUndo,
    canRedo,
    setUiState,
    handleNodesChange,
    handleEdgesChange,
    handleConnect,
    saveWorkflow,
    deleteSelectedConnection,
    handleStepCreated,
    handleStepUpdated,
    undo,
    redo,
    onNameChange,
    onDescriptionChange,
    runWithLoading,
  } = useWorkflowEditor(workflow, onWorkflowUpdate, handleTest);

  // Execution tracking — uses DOM classes to avoid React state re-renders
  const executionPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  nodesRef.current = nodes;
  edgesRef.current = edges;
  const stepFormSubmitRef = useRef<(() => Promise<void>) | null>(null);

  const clearExecutionClasses = useCallback(() => {
    document.querySelectorAll(".react-flow__node").forEach((el) => {
      el.classList.remove(
        "execution-pending",
        "execution-running",
        "execution-success",
        "execution-failure",
      );
    });
  }, []);

  const findNodeEl = useCallback(
    (key: string): Element | null =>
      document.querySelector(`[data-id="${key}"]`) ||
      document.querySelector(`[data-testid="rf__node-${key}"]`),
    [],
  );

  const startExecutionTracking = useCallback(() => {
    // Stop any existing poll
    if (executionPollRef.current) {
      clearInterval(executionPollRef.current);
      executionPollRef.current = null;
    }
    clearExecutionClasses();

    // Monotonic state — once a step reaches a final state, it never goes back
    const stepStates = new Map<string, "running" | "success" | "failure">();
    // 5s grace covers clock skew + trigger→queue→DB latency for fast workflows that finish before the first poll.
    const trackingAnchor = Date.now() - 5000;

    // Build execution order from edges (start → step1 → step2 → ...)
    const getExecutionOrder = (): string[] => {
      const order: string[] = [];
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;
      let currentId = "start-node";
      const visited = new Set<string>();
      while (true) {
        const nextEdge = currentEdges.find(
          (e) => e.source === currentId && !visited.has(e.target),
        );
        if (!nextEdge) break;
        const nextNode = currentNodes.find((n) => n.id === nextEdge.target);
        if (!nextNode || nextNode.type !== "stepNode") break;
        visited.add(nextNode.id);
        order.push(nextNode.id);
        currentId = nextNode.id;
      }
      return order;
    };

    const executionOrder = getExecutionOrder();

    // Mark first step as running immediately
    if (executionOrder.length > 0) {
      stepStates.set(executionOrder[0], "running");
      const el = findNodeEl(executionOrder[0]);
      if (el) el.classList.add("execution-running");
    }

    const applyStates = () => {
      clearExecutionClasses();
      for (const [key, state] of stepStates) {
        const el = findNodeEl(key);
        if (el) el.classList.add(`execution-${state}`);
      }
    };

    const pollFn = async () => {
      try {
        const jobs = await workflowService.getWorkflowJobHistory(
          workflow.id,
          1,
          1,
        );
        const latestJob = (jobs as any)?.items?.[0] || (jobs as any)?.data?.[0];
        if (!latestJob) return;

        const status = (latestJob.status || "").toLowerCase();

        const createdAt = latestJob.created_at ? new Date(latestJob.created_at).getTime() : NaN;
        if (Number.isFinite(createdAt) && createdAt < trackingAnchor) return;

        // Process completed steps from the API — only advance, never go back
        const apiCompletedKeys = new Set<string>();
        latestJob.steps?.forEach((s: any) => {
          const stepKey = s.workflow_step?.key;
          if (!stepKey) return;
          apiCompletedKeys.add(stepKey);
          const finalState = s.success ? "success" : "failure";
          stepStates.set(stepKey, finalState);
        });

        // If job is still running, find the first step in execution order that
        // isn't completed and mark it as running
        if (status === "running" || status === "pending") {
          for (const stepId of executionOrder) {
            const current = stepStates.get(stepId);
            if (current === "success" || current === "failure") continue;
            // This is the current running step
            stepStates.set(stepId, "running");
            break;
          }
        }

        applyStates();

        // Stop when done
        if (status !== "running" && status !== "pending") {
          if (executionPollRef.current) {
            clearInterval(executionPollRef.current);
            executionPollRef.current = null;
          }
          setTimeout(clearExecutionClasses, 5000);
        }
      } catch (e) {
        /* ignore */
      }
    };

    // Poll every 500ms, starting after a brief delay for the job to be created
    setTimeout(() => {
      pollFn();
      executionPollRef.current = setInterval(pollFn, 500);
    }, 300);
  }, [workflow.id, clearExecutionClasses, findNodeEl]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (executionPollRef.current) clearInterval(executionPollRef.current);
    };
  }, []);

  const closeDrawers = () => {
    setUiState((s) => ({
      ...s,
      selectedStep: null,
      drawers: { addStep: false, editStep: false },
    }));
  };

  const handleAddStep = useCallback(() => {
    setUiState((s) => ({ ...s, drawers: { ...s.drawers, addStep: true } }));
  }, [setUiState]);

  const handleSettingsSave = async (values: Partial<Workflow>) => {
    await runWithLoading(async () => {
      try {
        const updateData: CreateUpdateWorkflow = {
          name: values.name || workflowState.name,
          description: values.description || workflowState.description,
          group: values.group !== undefined ? values.group : workflowState.group,
          enabled:
            values.enabled !== undefined ? values.enabled : workflowState.enabled,
          editor_state: values.editor_state || workflowState.editor_state,
          triggers: (values.triggers ?? workflowState.triggers ?? []).map((t) => ({
            type: t.type,
            enabled: t.enabled,
            config: t.config,
          })),
        };

        await workflowService.updateWorkflow(workflow.id, updateData);
        toast.success("Workflow settings updated successfully");
        if (onWorkflowUpdate) {
          onWorkflowUpdate();
        }
      } catch (error) {
        console.error("Failed to update workflow settings:", error);
        toast.error("Failed to update workflow settings");
      }
    });
  };

  const onTriggerWorkflow = useCallback(() => {
    setIsTriggerWorkflowDialogOpen(true);
  }, []);

  // Show confirmation dialog if the user tries to exit with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (workflowState.hasUnsavedChanges) {
        e.preventDefault();
        // For most browsers
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [workflowState.hasUnsavedChanges]);

  return (
    <Flex direction="column" height="100%" style={{ overflow: "hidden" }}>
      {/* Inject custom styles for React Flow controls */}
      <style dangerouslySetInnerHTML={{ __html: reactFlowControlsStyles }} />

      <WorkflowToolbar
        workflow={workflow}
        workflowName={workflowState.name}
        workflowDescription={workflowState.description}
        isLoading={workflowState.isLoading}
        hasUnsavedChanges={workflowState.hasUnsavedChanges}
        selectedEdge={workflowState.selectedEdge}
        canUndo={canUndo}
        canRedo={canRedo}
        onAddStep={handleAddStep}
        onDeleteConnection={() => {
          deleteSelectedConnection();
          setEdgeDeletePos(null);
        }}
        onSave={saveWorkflow}
        onTriggerWorkflow={onTriggerWorkflow}
        onShowHistory={() =>
          setUiState((s) => ({ ...s, showJobHistory: true }))
        }
        onShowSettings={() => setUiState((s) => ({ ...s, showSettings: true }))}
        onEnableWorkflow={onEnableWorkflow}
        onDisableWorkflow={onDisableWorkflow}
        onUndo={undo}
        onRedo={redo}
        onNameChange={onNameChange}
        onDescriptionChange={onDescriptionChange}
      />

      <Box
        flexGrow="1"
        position="relative"
        style={{ overflow: "hidden", backgroundColor: "var(--gray-2)" }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={handleConnect}
          onEdgeClick={(event, edge) => {
            setUiState((s) => ({ ...s, selectedEdge: edge }));
            const container = (event.target as HTMLElement).closest(
              ".react-flow",
            );
            if (container) {
              const rect = container.getBoundingClientRect();
              setEdgeDeletePos({
                x: (event as any).clientX - rect.left,
                y: (event as any).clientY - rect.top,
              });
            }
          }}
          onPaneClick={() => {
            setUiState((s) => ({ ...s, selectedEdge: null }));
            setEdgeDeletePos(null);
          }}
          nodeTypes={nodeTypes}
          connectionLineType={ConnectionLineType.SmoothStep}
          connectionLineStyle={{
            stroke: "var(--accent-9)",
            strokeWidth: 2,
            strokeDasharray: "5 5",
          }}
          fitView
          snapToGrid
          snapGrid={[15, 15]}
          defaultEdgeOptions={{
            animated: true,
            style: { stroke: "#2563eb", strokeWidth: 2 },
          }}
          selectNodesOnDrag={false}
          proOptions={{ hideAttribution: true }}
          className="[&_.react-flow__pane]:cursor-default [&_.react-flow__handle]:cursor-crosshair [&_.react-flow__node]:cursor-move"
        >
          <Background />
          <Controls />
        </ReactFlow>

        {edgeDeletePos && workflowState.selectedEdge && (
          <div
            onClick={() => {
              deleteSelectedConnection();
              setEdgeDeletePos(null);
            }}
            title="Remove connection"
            style={{
              position: "absolute",
              left: edgeDeletePos.x,
              top: edgeDeletePos.y,
              transform: "translate(-50%, -50%)",
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "var(--red-9)",
              border: "2px solid white",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              zIndex: 10,
              display: "grid",
              placeItems: "center",
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M1 1L9 9M9 1L1 9"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
      </Box>

      <SideDrawer
        isOpen={workflowState.drawers.addStep}
        onClose={closeDrawers}
        title="Create Step"
        width="max-w-2xl"
        saveButton={{
          label: "Add Step",
          loading: false,
          onClick: () => {
            stepFormSubmitRef.current?.();
          },
        }}
      >
        <StepForm
          existingSteps={workflowState.steps}
          triggerEntity={resolveTriggerEntity(workflowState.triggers)}
          onClose={closeDrawers}
          onStepCreated={handleStepCreated}
          onReady={(api) => { stepFormSubmitRef.current = api.submit; }}
        />
      </SideDrawer>

      <SideDrawer
        isOpen={workflowState.drawers.editStep}
        onClose={closeDrawers}
        title="Edit Step"
        width="max-w-2xl"
        saveButton={{
          label: "Update Step",
          loading: false,
          onClick: () => {
            stepFormSubmitRef.current?.();
          },
        }}
      >
        {workflowState.selectedStep && (
          <StepForm
            existingSteps={workflowState.steps.filter(
              (s) => s.id !== workflowState.selectedStep!.id,
            )}
            triggerEntity={resolveTriggerEntity(workflowState.triggers)}
            step={workflowState.selectedStep}
            onClose={closeDrawers}
            onStepUpdated={(stepData) =>
              handleStepUpdated(workflowState.selectedStep!.id, stepData)
            }
            onReady={(api) => { stepFormSubmitRef.current = api.submit; }}
          />
        )}
      </SideDrawer>

      <Modal
        isOpen={workflowState.showJobHistory}
        onClose={() => setUiState((s) => ({ ...s, showJobHistory: false }))}
        title="Workflow Job History"
        description="View and analyze workflow execution history"
        size="full"
        className="h-[90vh]"
      >
        <WorkflowJobHistory workflowId={workflow.id} />
      </Modal>

      <Modal
        isOpen={workflowState.showSettings}
        onClose={() => setUiState((s) => ({ ...s, showSettings: false }))}
        title="Workflow Settings"
        description="Configure workflow trigger and behavior"
      >
        <WorkflowSettings
          workflow={workflowState}
          isSubmitting={workflowState.isLoading}
          extraFields={
            <InputField
              name="group"
              label="Group"
              description="Optional. Group related workflows (e.g. 'Business logic', 'Comms') so you can filter them on the list page."
            />
          }
          onSave={handleSettingsSave}
          onClose={() => setUiState((s) => ({ ...s, showSettings: false }))}
        />
      </Modal>

      <TriggerWorkflowDialog
        workflow={workflow}
        isOpen={isTriggerWorkflowDialogOpen}
        onSuccess={() => {
          setIsTriggerWorkflowDialogOpen(false);
          startExecutionTracking();
        }}
        onClose={() => setIsTriggerWorkflowDialogOpen(false)}
      />
    </Flex>
  );
};

// Pick a single trigger entity for the variable picker context. Event triggers
// expose one entity directly; manual triggers may have a list and we use the
// first; cron / webhook triggers have none.
function resolveTriggerEntity(triggers: WorkflowTrigger[] | undefined): string | undefined {
  if (!triggers) return undefined;
  for (const t of triggers) {
    if (t.config?.event_entity) return t.config.event_entity;
    if (t.config?.manual_entities && t.config.manual_entities.length > 0) {
      return t.config.manual_entities[0];
    }
  }
  return undefined;
}

export const WorkflowEditorProvider: FC<WorkflowEditorProps> = (props) => {
  return (
    <ReactFlowProvider>
      <WorkflowEditor {...props} />
    </ReactFlowProvider>
  );
};
